import { REGEX } from '@flatsy/core/Form/ReactFormHooks/Field/Constant'
import FlatsyTextField from '@flatsy/core/Form/ReactFormHooks/Field/Text'
import { TextError } from '@flatsy/core/Form/ReactFormHooks/Field/TextError'
import Separator from '@flatsy/core/Layout/Separator'
import { LabelSeparator } from '@flatsy/core/MaterialUi/Typo'
import i18n from '@flatsy/utils/translations'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Grid } from '@material-ui/core'
import React, { useContext, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { FormContext, ReturnContextProps } from 'src/containers/Qualification/Context'
import { useApplicantById } from 'src/hooks/ApplicantsSelector'
import * as yup from 'yup'
import { useFormStyles } from '../Style'
import { SharedProps } from '../Type'
import { CoApplicantData } from './Type'

const VerlingueGuarantor: React.FC<SharedProps> = ({ renderMobileFormButton }) => {
  const formClasses = useFormStyles()
  const {
    wizardContext: { context },
    saveApplicantAndContinue,
  } = useContext(FormContext) as ReturnContextProps

  const applicant = useApplicantById(context.applicantId)

  const schema = yup.object({
    first_name: yup.string().required(TextError.empty).typeError(TextError.empty),
    last_name: yup.string().required(TextError.empty).typeError(TextError.empty),
  })

  const schemaVerlingue = yup.object({
    first_name: yup.string().required(TextError.empty).typeError(TextError.empty),
    last_name: yup.string().required(TextError.empty).typeError(TextError.empty),
    phone_number: yup
      .string()
      .matches(
        REGEX.phone,
        i18n.t('the_phone_number_entered_does_not_match_the_correct_formatting')
      )
      .required(),
    email: yup
      .string()
      .email(i18n.t('the_email_address_entered_does_not_match_the_correct_formatting'))
      .matches(
        REGEX.email,
        i18n.t('the_email_address_entered_does_not_match_the_correct_formatting')
      )
      .required(),
  })

  const returnSchema = () => {
    if (context.application?.is_verlingue_supported) {
      return schemaVerlingue
    }
    return schema
  }

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitSuccessful, isValid, isDirty },
  } = useForm<CoApplicantData>({
    mode: 'all',
    resolver: yupResolver(returnSchema()),
  })

  useEffect(() => {
    if (applicant) {
      reset({
        first_name: '',
        last_name: '',
        phone_number: '',
        email: '',
      })
    }
  }, [applicant])

  const onSubmit = async (values: CoApplicantData): Promise<undefined> => {
    if (applicant) {
      saveApplicantAndContinue(applicant, values)
    }
    return undefined
  }

  if (!applicant) {
    return null
  }

  return (
    <div className={formClasses.container}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <LabelSeparator align="center">{i18n.t('guarantor_s_information')}</LabelSeparator>
        <Grid container spacing={1}>
          <Grid sm={6} xs={12} item>
            <Controller
              name="first_name"
              control={control}
              render={({ field, fieldState }) => (
                <FlatsyTextField
                  label={i18n.t('first_name')}
                  type="text"
                  {...field}
                  {...fieldState}
                  isSubmitSuccessful={isSubmitSuccessful}
                />
              )}
            />
          </Grid>
          <Grid sm={6} xs={12} item>
            <Controller
              name="last_name"
              control={control}
              render={({ field, fieldState }) => (
                <FlatsyTextField
                  label={i18n.t('last_name')}
                  type="text"
                  {...field}
                  {...fieldState}
                  isSubmitSuccessful={isSubmitSuccessful}
                />
              )}
            />
          </Grid>
        </Grid>
        {context.application?.is_verlingue_supported && (
          <>
            <Separator size="32px" />
            <LabelSeparator align="center">
              {i18n.t('give_us_the_means_to_reach_this_person')}
            </LabelSeparator>
            <Grid container spacing={1}>
              <Grid sm={6} xs={12} item>
                <Controller
                  name="phone_number"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FlatsyTextField
                      label={i18n.t('phone')}
                      type="text"
                      {...field}
                      {...fieldState}
                      isSubmitSuccessful={isSubmitSuccessful}
                    />
                  )}
                />
              </Grid>
              <Grid sm={6} xs={12} item>
                <Controller
                  name="email"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FlatsyTextField
                      label={i18n.t('email_address')}
                      type="email"
                      {...field}
                      {...fieldState}
                      isSubmitSuccessful={isSubmitSuccessful}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </>
        )}
        {!renderMobileFormButton ? (
          <Button
            variant="contained"
            color="primary"
            disableElevation
            type="submit"
            className={formClasses.submit}
            disabled={!isValid}
          >
            {i18n.t('continue')}
          </Button>
        ) : (
          renderMobileFormButton(isDirty, !isValid)
        )}
      </form>
    </div>
  )
}

export default VerlingueGuarantor
